import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileImage, faFileLines, faFile, faArrowUpRightFromSquare, faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';
import MobileSearchFilter from './MobileSearchFilter';
import MobileSelect from './MobileSelect';

interface Props { access: any; baseUrl?: string; }

const fileKind = (url?: string) => {
  const ext = (url || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return { icon: faFilePdf, color: '#dc2626' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return { icon: faFileImage, color: '#0891b2' };
  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) return { icon: faFileLines, color: '#2563eb' };
  return { icon: faFile, color: '#64748b' };
};

const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

interface AddForm {
  title: string;
  description: string;
  categoryId: string;
  dayLearn: string;
  isForAll: boolean;
  userIds: string[];
}
const emptyAdd: AddForm = { title: '', description: '', categoryId: '', dayLearn: '10', isForAll: true, userIds: [] };

const MobileDocuments: React.FC<Props> = ({ access, baseUrl = '' }) => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const { enqueueSnackbar } = useSnackbar();
  const canAdd = access?.document_add === true;

  const [cats, setCats] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<any | null>(null);
  const [viewer, setViewer] = useState<any | null>(null); // просмотр HTML-контента созданного документа
  const [busy, setBusy] = useState(false);

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<AddForm>(emptyAdd);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async (cat: string) => {
    try {
      const { data } = cat === 'all'
        ? await axios.get('document')
        : await axios.get(`document/category/${cat}`);
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      setDocs([]);
    }
  }, []);

  useEffect(() => {
    if (!auth) return;
    (async () => {
      setLoading(true);
      try {
        const [c, , u] = await Promise.allSettled([axios.get('document-category'), fetchDocs('all'), axios.get('users')]);
        if (c.status === 'fulfilled') setCats(Array.isArray(c.value.data) ? c.value.data : []);
        if (u.status === 'fulfilled') setUsers(Array.isArray(u.value.data) ? u.value.data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, fetchDocs]);

  useEffect(() => { if (auth && !loading) fetchDocs(catFilter); }, [auth, catFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) => `${d.title || ''} ${d.description || ''}`.toLowerCase().includes(q));
  }, [docs, search]);

  const catName = useCallback((id: any) => cats.find((c) => c.id === id)?.name || '', [cats]);

  const statusOf = (doc: any) => {
    const need = doc.needToLearnByUser;
    if (!need) return null;
    return need.status === 'learned' ? 'fulfilled' : 'pending';
  };
  const statusLabel = (s: string | null) =>
    s === 'fulfilled' ? t('PolicyStatusAcknowledged') : s === 'pending' ? t('PolicyStatusPending') : '';

  const fileHref = (doc: any) =>
    doc?.file_url ? `${baseUrl.replace(/\/$/, '')}/${doc.file_url.replace(/^\.?\//, '')}` : '';

  const openDoc = (doc: any) => {
    const href = fileHref(doc);
    if (href) {
      // надёжнее window.open на мобильных браузерах
      const a = document.createElement('a');
      a.href = href; a.target = '_blank'; a.rel = 'noopener noreferrer';
      document.body.appendChild(a); a.click(); a.remove();
    } else if (doc?.content) {
      setViewer(doc);
    }
  };

  const acknowledge = async (doc: any) => {
    setBusy(true);
    try {
      await axios.put(`document-learn/status/${doc.id}`, { status: 'learned' });
      await fetchDocs(catFilter);
      setDetail((d: any) => (d ? { ...d, needToLearnByUser: { ...(d.needToLearnByUser || {}), status: 'learned' } } : d));
      enqueueSnackbar(t('PolicyStatusAcknowledged') as string, { variant: 'success' });
    } catch {
      enqueueSnackbar(t('SaveError') as string, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const openAdd = () => { setForm(emptyAdd); setFile(null); setAdding(true); };

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) { enqueueSnackbar(t('OnlyPdf') as string, { variant: 'error' }); return; }
    if (f.size > 10 * 1024 * 1024) { enqueueSnackbar('Max 10MB', { variant: 'error' }); return; }
    setFile(f);
  };

  const submitAdd = async () => {
    if (!form.title.trim()) { enqueueSnackbar(t('Title') as string, { variant: 'error' }); return; }
    if (!form.categoryId) { enqueueSnackbar(t('SelectCategory') as string, { variant: 'error' }); return; }
    if (!form.isForAll && form.userIds.length === 0) { enqueueSnackbar(t('ChooseUsers') as string, { variant: 'error' }); return; }

    setBusy(true);
    try {
      const metadata: any = {
        title: form.title,
        description: form.description,
        documentCategoryId: Number(form.categoryId),
        isForAll: form.isForAll,
        isExternal: !!file,
        day_learn: Number(form.dayLearn) || 0,
      };
      if (!form.isForAll) metadata.userId = form.userIds.map(Number);

      const res = await axios.post('/document/add', metadata);
      const id = res.data?.id;
      if (!id) throw new Error('no id');

      if (file) {
        const fd = new FormData();
        fd.append('document', file);
        await axios.post(`document/upload/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      setAdding(false);
      await fetchDocs(catFilter);
    } catch {
      enqueueSnackbar(t('SaveError') as string, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingCenter />;

  const detailMeta = (d: any) => ([
    [t('Category'), catName(d.documentCategoryId)],
    [t('Type'), (d.file_url ? (d.file_url.split('.').pop() || '').toUpperCase() : (d.content ? t('Text') : ''))],
    [t('Added'), fmtDate(d.createdAt)],
  ] as [any, any][]).filter(([, v]) => v);

  return (
    <div className="flex flex-col gap-[10px]">
      <MobileSearchFilter
        value={search}
        onChange={setSearch}
        options={[{ value: 'all', label: t('All') }, ...cats.map((c) => ({ value: String(c.id), label: c.name }))]}
        filterValue={catFilter}
        onFilter={setCatFilter}
      />

      {filtered.length === 0 ? (
        <EmptyState icon="📁" message={t('NoDocuments') as string} />
      ) : (
        <div className="m-list">
          {filtered.map((d) => {
            const k = fileKind(d.file_url);
            const st = statusOf(d);
            return (
              <button type="button" key={d.id} className="m-li" onClick={() => setDetail(d)}>
                <span className="m-li-ic" style={{ background: `${k.color}22`, color: k.color }}>
                  <FontAwesomeIcon icon={k.icon} />
                </span>
                <span className="m-li-main">
                  <span className="m-li-title">{d.title || '—'}</span>
                  {d.description && <span className="m-li-sub">{d.description}</span>}
                </span>
                {st && <span className={`sbadge ${st}`}>{statusLabel(st)}</span>}
              </button>
            );
          })}
        </div>
      )}

      {canAdd && !adding && !detail && !viewer && (
        <button type="button" className="m-ts-fab" onClick={openAdd} aria-label={t('AddDocument') as string}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
      )}

      {/* ── Детали документа ── */}
      {detail && (
        <>
          <div className="m-sheet-scrim" onClick={() => setDetail(null)} />
          <div className="m-sheet">
            <div className="m-sheet-grab" />
            <div className="m-sheet-head">
              <div>
                <div className="m-sheet-title">{detail.title || '—'}</div>
                {statusOf(detail) && <div className="m-sheet-sub">{statusLabel(statusOf(detail))}</div>}
              </div>
            </div>
            <div className="m-sheet-body">
              {detail.description && <p className="text-[14px] text-[var(--text-color)] leading-[1.5] mb-[4px]">{detail.description}</p>}

              {detailMeta(detail).map(([label, value], i) => (
                <div key={i} className="m-kv"><span className="m-kv-lbl">{label}</span><span className="m-kv-val">{value}</span></div>
              ))}

              {(fileHref(detail) || detail.content) && (
                <button type="button" className="btn-primary m-doc-open" onClick={() => openDoc(detail)}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> {t('OpenDocument')}
                </button>
              )}
              {statusOf(detail) === 'pending' && (
                <button type="button" className="m-btn-outline" disabled={busy} onClick={() => acknowledge(detail)}>
                  <FontAwesomeIcon icon={faCheck} /> {t('Acknowledge')}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Просмотр HTML-контента (созданный документ) ── */}
      {viewer && (
        <div className="m-fs">
          <div className="m-fs-head">
            <button type="button" className="m-fs-back" onClick={() => setViewer(null)}>✕</button>
            <div className="m-fs-title">{viewer.title || '—'}</div>
            <div style={{ width: 36 }} />
          </div>
          <div className="m-fs-body">
            <div className="m-doc-content" dangerouslySetInnerHTML={{ __html: viewer.content }} />
          </div>
        </div>
      )}

      {/* ── Добавление документа ── */}
      {adding && (
        <div className="m-fs">
          <div className="m-fs-head">
            <button type="button" className="m-fs-back" onClick={() => setAdding(false)}>✕</button>
            <div className="m-fs-title">{t('AddDocument')}</div>
            <div style={{ width: 36 }} />
          </div>
          <div className="m-fs-body">
            <div className="m-ts-field"><label>{t('Title')} *</label>
              <input className="m-ts-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="m-ts-field"><label>{t('Description')}</label>
              <input className="m-ts-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="m-ts-two">
              <div className="m-ts-field"><label>{t('Category')} *</label>
                <MobileSelect
                  title={t('Category') as string}
                  value={form.categoryId}
                  onChange={(v) => setForm({ ...form, categoryId: v })}
                  placeholder={t('SelectCategory') as string}
                  options={cats.map((c) => ({ value: String(c.id), label: c.name }))}
                />
              </div>
              <div className="m-ts-field"><label>{t('DayLearn')}</label>
                <input className="m-ts-input" inputMode="numeric" value={form.dayLearn}
                  onChange={(e) => { if (/^\d*$/.test(e.target.value)) setForm({ ...form, dayLearn: e.target.value }); }} />
              </div>
            </div>

            <div className="m-ts-field"><label>PDF</label>
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={(e) => pickFile(e.target.files ? e.target.files[0] : null)} />
              <button type="button" className="m-ts-select" onClick={() => fileRef.current?.click()}>
                <span className={`m-ts-select-val${file ? '' : ' ph'}`}>{file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : (t('UploadFile') as string)}</span>
                <FontAwesomeIcon icon={faPlus} className="m-ts-select-chev" />
              </button>
            </div>

            {/* Аудитория */}
            <div className="m-ts-field"><label>{t('ChooseUsers')}</label>
              <div className="m-seg">
                <button type="button" className={`m-seg-btn${form.isForAll ? ' on' : ''}`} onClick={() => setForm({ ...form, isForAll: true })}>{t('AllUsers')}</button>
                <button type="button" className={`m-seg-btn${!form.isForAll ? ' on' : ''}`} onClick={() => setForm({ ...form, isForAll: false })}>{t('ChooseUsers')}</button>
              </div>
            </div>
            {!form.isForAll && (
              <div className="m-ts-field">
                <MobileSelect
                  multi
                  title={t('ChooseUsers') as string}
                  value={form.userIds}
                  onChange={(v) => setForm({ ...form, userIds: v })}
                  placeholder={t('ChooseUsers') as string}
                  options={users.map((u) => ({ value: String(u.id), label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email }))}
                />
              </div>
            )}
          </div>
          <div className="m-fs-foot">
            <button type="button" className="m-ts-cancel" onClick={() => setAdding(false)}>{t('Cancel')}</button>
            <button type="button" className="btn-primary" disabled={busy} onClick={submitAdd}>{t('Save')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDocuments;
